import {
  FIRST_DOWN_YARDAGE,
  KICKOFF_YARDLINE,
  PAT_YARDLINE,
  TEventTypes,
  TOUCHBACK_YARDLINE,
  TPlayDesigns,
  TPlayResultTypes,
  TQuarters,
} from '@/constants';
import {
  IEvent,
  IMachineContextCurrentPlay,
  IPlayFlow,
  IPlayflowPlay,
  TOffDef,
  TTeam,
  TTeamSide,
} from '@/types';
import { defineStore } from 'pinia';
import {
  adjustPlayerStats,
  computePlayerStats,
  determinePlayDriveDirection,
  getDriveNumber,
  getDriveNumberingFromCache,
  getFumbleOrPenaltyYardline,
  getPlayResultId,
  getPlayResults,
  getResultingSideOfField,
  getResultingYardline,
  isLastPlayKickoffPenalty,
  isNextPlayKickoff,
  isNoScoreAndNoPossessionChange,
  isNoScorePossessionChange,
  isOvertimeScoringPlay,
  isReKickoffPenalty,
  nullifyDownAndDistanceIfNeccessary,
  sendPlayFlowEvent,
} from './util/playFlowHelper';
import { find, findIndex, findLast, findLastIndex, isEmpty, last } from 'lodash';
import { getUrlSearchParam, setUrlSearchParam } from '@/utilities';
import { useQueueManager } from './queueManager';
import moment from 'moment';

const CURRENT_PLAY_NUMBER_KEY = 'CURRENT_PLAY_NUMBER_KEY';

export const usePlayFlowState = defineStore('playFlow', {
  state: (): IPlayFlow => ({
    plays: [],
    /* side that team is defending */
    teams: {
      left: null,
      right: null,
    },
    driveNumber: 1,
    drivePlayNumber: 0,
    driveDirection: null,
    yardLine: null,
    down: null,
    distance: null,
    activeTeam: null,
    currentQuarter: 1,
    editing: null,
    inserting: null,
    isQb1Out: {
      home: false,
      away: false,
    },
    startersOut: {
      home: false,
      away: false,
    },
    lastOverTimeScore: null,
    hasStartedOvertime: false,
    isStaleLastPlay: false,
    isEditedQbStatus: false,
    isMostRecentPlayPossessionEdited: null,
  }),

  actions: {
    /**
     * Parses game snapshot plays, and sets current play state
     */
    parsePlayState(gameID: number, plays: IPlayflowPlay[], setInitialState = false) {
      const queueManager = useQueueManager();

      if (setInitialState) {
        queueManager.dropDuplicateQueuedPlays(gameID, plays);
        queueManager.resumeQueuedPlayProcessing();
      }

      const playsArr = [...(plays || []), ...queueManager.currentQueuePlaysForDisplay];

      const lastValidPlay = findLast(playsArr, (play) => !play.isPlayNullified);
      const actualLastPlay = last(playsArr);

      if (
        !lastValidPlay ||
        !actualLastPlay ||
        ((queueManager.queue.isProcessingPlays || queueManager.queue.isProcessingPlayflowPuts) &&
          !setInitialState)
      ) {
        return;
      }

      this.isStaleLastPlay = moment(`${actualLastPlay.createdAt}Z`).isBefore(
        moment().subtract(5, 'minutes').utc()
      );

      this.plays = playsArr;
      this.down = lastValidPlay.resultingDown;
      this.distance = lastValidPlay.resultingDistance;

      // get cached last play number in the event of network failure/dropped plays
      let nextDriveNumber;
      let nextDrivePlayNumber;
      const cachedDriveNumbering = getDriveNumberingFromCache(
        gameID,
        CURRENT_PLAY_NUMBER_KEY,
        actualLastPlay
      );
      if (!isEmpty(cachedDriveNumbering)) {
        nextDriveNumber = cachedDriveNumbering.driveNumber;
        nextDrivePlayNumber = cachedDriveNumbering.drivePlayNumber;
      } else {
        const results = getDriveNumber(
          lastValidPlay,
          actualLastPlay.driveNumber,
          actualLastPlay.drivePlayNumber
        );
        nextDriveNumber = results.nextDriveNumber;
        nextDrivePlayNumber = results.nextDrivePlayNumber;
      }
      this.driveNumber = nextDriveNumber;
      this.drivePlayNumber = nextDrivePlayNumber;

      localStorage.setItem(
        CURRENT_PLAY_NUMBER_KEY,
        JSON.stringify({
          gameId: gameID,
          driveNumber: nextDriveNumber,
          drivePlayNumber: nextDrivePlayNumber,
        })
      );

      // NOTE if side not in query param, will be manually set by user
      const homeSide = getUrlSearchParam('homeSide') || TTeamSide.LEFT;

      this.setActiveTeam(lastValidPlay.homeTeamEndedWithPossession ? TTeam.HOME : TTeam.AWAY);

      if (homeSide === TTeamSide.LEFT) {
        this.teams = { left: TTeam.HOME, right: TTeam.AWAY };
        this.setDriveDirection(lastValidPlay.homeTeamEndedWithPossession ? TTeamSide.RIGHT : TTeamSide.LEFT);
      } else {
        this.teams = { left: TTeam.AWAY, right: TTeam.HOME };
        this.setDriveDirection(lastValidPlay.homeTeamEndedWithPossession ? TTeamSide.LEFT : TTeamSide.RIGHT);
      }

      // for PAT poss should stay with starting team regardless if they ended with possession
      // (i.e. if pat attempt returned by defense, PAT team still needs to kickoff) in regulation
      if (
        [TPlayDesigns.EXTRA_PT, TPlayDesigns.TWO_PT_CONVERSION].includes(lastValidPlay.playDesignId) &&
        this.currentQuarter <= TQuarters.Q4
      ) {
        this.setActiveTeam(lastValidPlay.homeTeamStartedWithPossession ? TTeam.HOME : TTeam.AWAY);
        if (homeSide === TTeamSide.LEFT) {
          this.teams = { left: TTeam.HOME, right: TTeam.AWAY };
          this.setDriveDirection(
            lastValidPlay.homeTeamStartedWithPossession ? TTeamSide.RIGHT : TTeamSide.LEFT
          );
        } else {
          this.teams = { left: TTeam.AWAY, right: TTeam.HOME };
          this.setDriveDirection(
            lastValidPlay.homeTeamStartedWithPossession ? TTeamSide.LEFT : TTeamSide.RIGHT
          );
        }
      }

      if (
        (homeSide === TTeamSide.LEFT && lastValidPlay.resultingIsHomeTeamSideOfField) ||
        (homeSide === TTeamSide.RIGHT && !lastValidPlay.resultingIsHomeTeamSideOfField)
      ) {
        this.setYardline(lastValidPlay.resultingYardLine, TTeamSide.LEFT);
      } else {
        this.setYardline(lastValidPlay.resultingYardLine, TTeamSide.RIGHT);
      }

      if (!this.isEditedQbStatus) {
        this.isQb1Out = {
          home: findLast(this.plays, (play) => play.homeTeamStartedWithPossession)?.qb1Out ?? false,
          away: findLast(this.plays, (play) => !play.homeTeamStartedWithPossession)?.qb1Out ?? false,
        };
      }
    },

    async pushPlay(gameId: number, currentPlay: IMachineContextCurrentPlay) {
      const {
        resultingDown,
        resultingDistance,
        homeTeamEndedWithPossession,
        isTurnoverOnDowns,
        isDefiniteFirstDown,
        distanceTraveled,
      } = getPlayResults(
        currentPlay,
        this.activeTeam,
        this.down,
        this.distance,
        this.driveDirection,
        this.yardLine.number,
        this.yardLine.side
      );

      if (isDefiniteFirstDown && !isTurnoverOnDowns) {
        this.pushEvent(
          {
            eventTypeId: TEventTypes.FIRST_DOWN,
            value: true,
          },
          gameId
        );
      }

      const play: IPlayflowPlay = {
        ...this.currentPlayInfo,
        resultingYardLine: getResultingYardline(currentPlay),
        resultingDown,
        resultingDistance,
        resultingIsHomeTeamSideOfField: getResultingSideOfField(
          this.teams,
          currentPlay.playDesignId,
          currentPlay.postPlaySide,
          currentPlay.penalty === TOffDef.DEFENSE
        ),
        homeTeamEndedWithPossession,
        playResultTypeId: getPlayResultId(currentPlay, this.currentTeamRoles.offense, isTurnoverOnDowns),
        refEventOutcomeId: currentPlay.refEventOutcomeId || null,
        playDesignId: currentPlay.playDesignId || TPlayDesigns.NO_PLAY,
        lateral: !!currentPlay.lateral,
        qb1Out:
          currentPlay.options?.isNonQb1Pass ?? this.isQb1Out[this.presentStateBasedOnCurrentMode.activeTeam],
        playerStats: computePlayerStats(currentPlay.players, currentPlay.resultType, distanceTraveled),
        ...getFumbleOrPenaltyYardline(this.teams, this.yardLine, currentPlay),
      };

      adjustPlayerStats(play, currentPlay);

      // for TDs/XPs/Kickoffs some starting/res down/distances will be null
      nullifyDownAndDistanceIfNeccessary(play);

      const lastValidPlay = findLast(this.plays, (p) => !p.isPlayNullified);
      if (
        [TPlayDesigns.KICKOFF, TPlayDesigns.ONSIDE].includes(play.playDesignId) &&
        !isReKickoffPenalty(currentPlay) &&
        !isLastPlayKickoffPenalty(lastValidPlay, play) &&
        play.drivePlayNumber !== 0
      ) {
        // ensure drive play number is 0 for kickoffs
        play.driveNumber += 1;
        play.drivePlayNumber = 0;
        this.incrementDriveNumber(gameId);
      }

      // for overtime scoring update the resulting field position accordingly
      if (isOvertimeScoringPlay(this.currentQuarter, currentPlay)) {
        play.resultingDistance = FIRST_DOWN_YARDAGE;
        play.resultingDown = 1;
        play.resultingIsHomeTeamSideOfField = !play.startingIsHomeTeamSideOfField;
        play.resultingYardLine = TOUCHBACK_YARDLINE;
        play.homeTeamEndedWithPossession = this.activeTeam !== TTeam.HOME;
      } else if (this.currentQuarter === TQuarters.OT3_PLUS) {
        play.resultingDistance = null;
        play.resultingDown = null;
        play.resultingIsHomeTeamSideOfField = this.teams[this.yardLine.side] !== TTeam.HOME;
        play.resultingYardLine = PAT_YARDLINE;
        play.homeTeamEndedWithPossession = this.activeTeam !== TTeam.HOME;
      }

      this.plays.push(play);
      this.isStaleLastPlay = false;
      this.isMostRecentPlayPossessionEdited = null;
      this.updatePlayState(
        gameId,
        currentPlay,
        play,
        play.homeTeamStartedWithPossession ? TTeam.HOME : TTeam.AWAY,
        isTurnoverOnDowns,
        play.resultingDown,
        play.resultingDistance,
        play.resultingYardLine
      );

      this.queuePlayflowUpdate(gameId, play);
    },

    updatePlayState(
      gameId: number,
      currentPlay: IMachineContextCurrentPlay,
      play: IPlayflowPlay,
      teamForPlay: TTeam,
      isTurnoverOnDowns: boolean,
      resultingDown: number | null,
      resultingDistance: number | null,
      resultingYardLine: number,
      isForPlayEdit = false
    ) {
      // Set up state for next play
      if (isOvertimeScoringPlay(this.currentQuarter, currentPlay)) {
        this.lastOverTimeScore = teamForPlay;
        const side = this.teams.left === this.activeTeam ? TTeamSide.LEFT : TTeamSide.RIGHT;
        this.initNewDrive(gameId, side);
        this.setYardline(resultingYardLine, side);
      } else if (isReKickoffPenalty(currentPlay)) {
        const currentSide = this.teams.left === this.activeTeam ? TTeamSide.LEFT : TTeamSide.RIGHT;

        this.down = null;
        this.distance = null;

        this.setYardline(resultingYardLine, currentSide);
        this.incrementDrivePlayNumber(gameId);
      } else if (isNextPlayKickoff(currentPlay)) {
        // field side will be side which the kicking team is defending
        let currentSide = this.teams.left === this.activeTeam ? TTeamSide.LEFT : TTeamSide.RIGHT;

        if (currentPlay.playDesignId === TPlayDesigns.KICKOFF) {
          // Kickoff saftey/TD field side will be opposite from kicking side
          currentSide = this.teams.left === this.activeTeam ? TTeamSide.RIGHT : TTeamSide.LEFT;
          this.setDriveDirection(currentSide === TTeamSide.LEFT ? TTeamSide.RIGHT : TTeamSide.LEFT);
        }

        this.down = null;
        this.distance = null;

        this.setYardline(resultingYardLine, currentSide);
        this.incrementDriveNumber(gameId);
      } else if (isNoScorePossessionChange(currentPlay, play, isTurnoverOnDowns)) {
        this.initNewDrive(
          gameId,
          this.driveDirection === TTeamSide.LEFT ? TTeamSide.RIGHT : TTeamSide.LEFT,
          currentPlay.options?.isKickoff,
          isForPlayEdit
        );
        this.distance = resultingDistance ?? FIRST_DOWN_YARDAGE;
        this.setYardline(resultingYardLine, currentPlay.postPlaySide);
      } else if (isNoScoreAndNoPossessionChange(currentPlay)) {
        // regular result (no score or poss change)
        const hasTOVResult = currentPlay.resultType.some((type) =>
          [TPlayResultTypes.TOV, TPlayResultTypes.FUMBLE_LOST, TPlayResultTypes.INTERCEPTION].includes(type)
        );
        if (currentPlay.options?.isKickReturnFumbleLost || hasTOVResult || currentPlay.options?.isNewDrive) {
          this.incrementDriveNumber(gameId);
        } else {
          this.incrementDrivePlayNumber(gameId);
        }
        this.down = resultingDown;
        this.distance = resultingDistance;
        this.setYardline(resultingYardLine, currentPlay.postPlaySide);
      } else {
        // should go to pat
        if (
          this.currentQuarter === TQuarters.OT3_PLUS &&
          !currentPlay.options.isTov &&
          !currentPlay.options.isKickoffOrPunt
        ) {
          this.incrementDriveNumber(gameId);
        } else {
          this.incrementDrivePlayNumber(gameId);
        }

        this.down = null;
        this.distance = null;
        this.setYardline(resultingYardLine, currentPlay.postPlaySide);

        // Switch team possession if a TD is scored on TOV/return
        // but not if a TD is scored on a kick return fumble lost
        if (
          (currentPlay.options?.isKickoffOrPunt ||
            currentPlay.options?.isTov ||
            this.currentQuarter === TQuarters.OT3_PLUS) &&
          !(currentPlay.options?.isKickReturnFumbleLost && currentPlay.options?.isScore)
        ) {
          this.switchActiveTeam();
          this.setDriveDirection(this.driveDirection === TTeamSide.LEFT ? TTeamSide.RIGHT : TTeamSide.LEFT);
        }
      }
    },

    pushEvent(event: IEvent, gameId: number) {
      // don't send events while in edit or insert mode
      if (
        !!this.inserting ||
        (!!this.editing && event.eventTypeId !== TEventTypes.REVISION) ||
        gameId === -1
      ) {
        return;
      }
      sendPlayFlowEvent(gameId, event);
    },

    queuePlayflowUpdate(gameId: number, play: IPlayflowPlay, isUpdate = false) {
      if (gameId > 0) {
        useQueueManager().enqueuePlay(gameId, play, isUpdate);
      }
    },

    setTeamSides(left: TTeam, right: TTeam) {
      this.teams = { left, right };
      const homeSideValue = left === TTeam.HOME ? 'left' : 'right';
      setUrlSearchParam('homeSide', homeSideValue);
    },

    setActiveTeam(team: TTeam) {
      this.activeTeam = team;
    },

    switchActiveTeam() {
      this.activeTeam = this.activeTeam === TTeam.HOME ? TTeam.AWAY : TTeam.HOME;
    },

    switchPoss(gameId?: number) {
      this.switchActiveTeam();
      this.setDriveDirection(this.driveDirection === TTeamSide.LEFT ? TTeamSide.RIGHT : TTeamSide.LEFT);

      const lastValidPlay = findLast(this.plays, (play) => !play.isPlayNullified);
      if (!lastValidPlay) {
        return;
      }

      // increment the drive if possession is switching and not already on new drive
      // handles switching back to original team and restoring original drive/play
      // numbering
      if (!this.isMostRecentPlayPossessionEdited && this.drivePlayNumber > 0) {
        this.isMostRecentPlayPossessionEdited = {
          originalNextDriveNumber: this.driveNumber,
          originalNextDrivePlayNumber: this.drivePlayNumber,
        };
        this.incrementDriveNumber(gameId);
      } else if (!isEmpty(this.isMostRecentPlayPossessionEdited)) {
        this.setCurrentPlayNumbering(
          gameId,
          this.isMostRecentPlayPossessionEdited.originalNextDriveNumber,
          this.isMostRecentPlayPossessionEdited.originalNextDrivePlayNumber
        );
        this.isMostRecentPlayPossessionEdited = null;
      }

      lastValidPlay.homeTeamEndedWithPossession = this.activeTeam === TTeam.HOME;

      // update ending possession of most recent play
      if (gameId) {
        this.queuePlayflowUpdate(gameId, lastValidPlay, true);
      }
    },

    setDriveDirection(direction: TTeamSide) {
      this.driveDirection = direction;
    },

    setYardline(yardline: number, currentSide: TTeamSide) {
      this.yardLine = {
        number: yardline,
        side: currentSide,
      };
    },

    initNewDrive(gameId: number, driveDirection: TTeamSide, isKickoff = false, isForPlayEdit = false) {
      this.down = 1;
      this.distance = FIRST_DOWN_YARDAGE;
      this.driveDirection = driveDirection;

      if (!isKickoff && !isForPlayEdit) {
        this.incrementDriveNumber(gameId);
      } else if (!isForPlayEdit) {
        this.incrementDrivePlayNumber(gameId);
      }
      this.switchActiveTeam();
    },

    setCurrentPlayNumbering(gameId: number, drive: number, play: number) {
      this.driveNumber = drive;
      this.drivePlayNumber = play;

      localStorage.setItem(
        CURRENT_PLAY_NUMBER_KEY,
        JSON.stringify({
          gameId,
          driveNumber: this.driveNumber,
          drivePlayNumber: this.drivePlayNumber,
        })
      );
    },

    incrementDriveNumber(gameId: number) {
      this.driveNumber += 1;
      this.drivePlayNumber = 0;

      localStorage.setItem(
        CURRENT_PLAY_NUMBER_KEY,
        JSON.stringify({
          gameId,
          driveNumber: this.driveNumber,
          drivePlayNumber: this.drivePlayNumber,
        })
      );
    },

    incrementDrivePlayNumber(gameId: number) {
      if (this.isValidDrivePlayNumberIncrement()) {
        this.drivePlayNumber += 1;
      }

      localStorage.setItem(
        CURRENT_PLAY_NUMBER_KEY,
        JSON.stringify({
          gameId,
          driveNumber: this.driveNumber,
          drivePlayNumber: this.drivePlayNumber,
        })
      );
    },

    isValidDrivePlayNumberIncrement(): boolean {
      const playForCurrentNumbering = find(
        this.plays,
        (p: IPlayflowPlay) => p.driveNumber === this.driveNumber && p.drivePlayNumber === this.drivePlayNumber
      );

      if (!isEmpty(playForCurrentNumbering)) {
        return true;
      }
      return false;
    },

    flipField() {
      const { left, right } = this.teams;
      this.setTeamSides(right, left);
      this.setDriveDirection(this.driveDirection === TTeamSide.LEFT ? TTeamSide.RIGHT : TTeamSide.LEFT);
      this.setYardline(
        this.yardLine.number,
        this.yardLine.side === TTeamSide.LEFT ? TTeamSide.RIGHT : TTeamSide.LEFT
      );
    },

    async updateLastPlay(
      update: {
        down?: number;
        distance?: number;
        yardline?: number;
        side?: TTeamSide;
        isNullified?: boolean;
      },
      gameId: number
    ) {
      const lastValidPlay = findLast(this.plays, (play) => !play.isPlayNullified);
      if (!lastValidPlay) {
        return;
      }

      lastValidPlay.resultingDistance = update.distance ?? lastValidPlay.resultingDistance;
      lastValidPlay.resultingDown = update.down ?? lastValidPlay.resultingDown;
      lastValidPlay.resultingYardLine = update.yardline ?? lastValidPlay.resultingYardLine;
      lastValidPlay.resultingIsHomeTeamSideOfField = update.side
        ? this.teams[update.side] === TTeam.HOME
        : lastValidPlay.resultingIsHomeTeamSideOfField;
      lastValidPlay.isPlayNullified = update.isNullified ?? lastValidPlay.isPlayNullified;

      this.down = lastValidPlay.resultingDown;
      this.distance = lastValidPlay.resultingDistance;
      this.setYardline(lastValidPlay.resultingYardLine, update.side ?? this.yardLine.side);

      this.pushEvent(
        {
          eventTypeId: TEventTypes.REVISION,
          value: true,
        },
        gameId
      );

      this.queuePlayflowUpdate(gameId, lastValidPlay, true);
    },

    revertToLastValidPlayState(beforeIndex?: number) {
      const lastValidPlay = findLast(this.plays.slice(0, beforeIndex), (play) => !play.isPlayNullified);
      if (!lastValidPlay) {
        return;
      }

      this.down = lastValidPlay.resultingDown;
      this.distance = lastValidPlay.resultingDistance;

      if (
        (this.teams.left === TTeam.HOME && lastValidPlay.resultingIsHomeTeamSideOfField) ||
        (this.teams.right === TTeam.HOME && !lastValidPlay.resultingIsHomeTeamSideOfField)
      ) {
        this.setYardline(lastValidPlay.resultingYardLine, TTeamSide.LEFT);
      } else {
        this.setYardline(lastValidPlay.resultingYardLine, TTeamSide.RIGHT);
      }

      this.setActiveTeam(lastValidPlay.homeTeamEndedWithPossession ? TTeam.HOME : TTeam.AWAY);

      if (this.teams.left === TTeam.HOME) {
        this.setDriveDirection(lastValidPlay.homeTeamEndedWithPossession ? TTeamSide.RIGHT : TTeamSide.LEFT);
      } else {
        this.setDriveDirection(lastValidPlay.homeTeamEndedWithPossession ? TTeamSide.LEFT : TTeamSide.RIGHT);
      }

      // if overturning a poss changing play, revert drive numbering
      // but continue drive play numbering on
      if (
        !beforeIndex &&
        ([
          TPlayResultTypes.TOV,
          TPlayResultTypes.INTERCEPTION,
          TPlayResultTypes.FUMBLE_LOST,
          TPlayResultTypes.CONVERSION_2PT,
          TPlayResultTypes.EXTRA_PT,
          TPlayResultTypes.CONVERSION_2PT,
          TPlayResultTypes.PAT_MISS,
          TPlayResultTypes.FG,
          TPlayResultTypes.FG_MISS,
        ].includes(this.lastPlay.playResultTypeId) ||
          this.lastPlay.homeTeamEndedWithPossession !== this.lastPlay.homeTeamStartedWithPossession)
      ) {
        this.driveNumber = lastValidPlay.driveNumber;
        this.drivePlayNumber = this.lastPlay.drivePlayNumber + 1;
      }
    },

    setQuarter(quarter: string) {
      switch (quarter) {
        case 'Q1':
          this.currentQuarter = 1;
          break;
        case 'Q2':
          this.currentQuarter = 2;
          break;
        case 'Q3':
          this.currentQuarter = 3;
          break;
        case 'Q4':
          this.currentQuarter = 4;
          break;
        case 'OT1':
          this.currentQuarter = 5;
          break;
        case 'OT2':
          this.currentQuarter = 6;
          break;
        case 'OT3+':
          this.currentQuarter = 7;
          break;
        default:
      }
    },

    getNextOvertimeTeam(): TTeam {
      if (this.lastOverTimeScore) {
        const side = this.lastOverTimeScore === TTeam.HOME ? TTeam.HOME : TTeam.AWAY;
        this.lastOverTimeScore = null;
        return side;
      }
      return this.activeTeam;
    },

    initEditPlay(play: IPlayflowPlay) {
      this.inserting = null;
      this.editing = {
        drive: play.driveNumber,
        play: play.drivePlayNumber,
      };
    },

    cancelEditOrInsertPlay() {
      this.editing = null;
      this.inserting = null;
    },

    initInsertPlay(play: IPlayflowPlay) {
      const { nextDriveNumber, nextDrivePlayNumber } = getDriveNumber(
        play,
        play.driveNumber,
        play.drivePlayNumber,
        true,
        this.plays
      );

      this.editing = null;
      this.inserting = {
        drive: nextDriveNumber,
        play: nextDrivePlayNumber,
        beforeDrive: play.driveNumber,
        beforePlay: play.drivePlayNumber,
      };
    },

    async commitPlayInsert(gameId: number, playForInsertion: IMachineContextCurrentPlay) {
      const preceedingPlayNdx = findIndex(
        this.plays,
        (p) => p.drivePlayNumber === this.inserting.beforePlay && p.driveNumber === this.inserting.beforeDrive
      );
      const preceedingPlay = this.plays[preceedingPlayNdx];
      const preceedingActiveTeam = preceedingPlay.homeTeamEndedWithPossession ? TTeam.HOME : TTeam.AWAY;

      const driveDirection = determinePlayDriveDirection(preceedingPlay, this.teams, true);

      let preceedingSide = TTeamSide.LEFT;
      if (
        (preceedingPlay.resultingIsHomeTeamSideOfField && this.teams.right === TTeam.HOME) ||
        (!preceedingPlay.resultingIsHomeTeamSideOfField && this.teams.left === TTeam.HOME)
      ) {
        preceedingSide = TTeamSide.RIGHT;
      }

      const {
        resultingDistance,
        resultingDown,
        homeTeamEndedWithPossession,
        isTurnoverOnDowns,
        distanceTraveled,
      } = getPlayResults(
        playForInsertion,
        preceedingActiveTeam,
        preceedingPlay.resultingDown,
        preceedingPlay.resultingDistance,
        driveDirection,
        preceedingPlay.resultingYardLine,
        preceedingSide
      );

      const resultingYardLine = getResultingYardline(playForInsertion);
      const resultingIsHomeTeamSideOfField = getResultingSideOfField(
        this.teams,
        playForInsertion.playDesignId,
        playForInsertion.postPlaySide,
        playForInsertion.penalty === TOffDef.DEFENSE
      );

      const playInsert: IPlayflowPlay = {
        driveNumber: this.inserting.drive,
        drivePlayNumber: this.inserting.play,
        startingYardLine: preceedingPlay.resultingYardLine,
        startingDown: preceedingPlay.resultingDown,
        startingDistance: preceedingPlay.resultingDistance,
        startingIsHomeTeamSideOfField: preceedingPlay.resultingIsHomeTeamSideOfField,
        homeTeamStartedWithPossession: preceedingPlay.homeTeamEndedWithPossession,
        isPlayNullified: false,
        resultingYardLine,
        resultingDown,
        resultingDistance,
        resultingIsHomeTeamSideOfField,
        homeTeamEndedWithPossession,
        playResultTypeId: getPlayResultId(playForInsertion, this.currentTeamRoles.offense, isTurnoverOnDowns),
        refEventOutcomeId: playForInsertion.refEventOutcomeId || null,
        playDesignId: playForInsertion.playDesignId || TPlayDesigns.NO_PLAY,
        lateral: !!playForInsertion.lateral,
        qb1Out:
          playForInsertion.options?.isNonQb1Pass ??
          this.isQb1Out[this.presentStateBasedOnCurrentMode.activeTeam],
        playerStats: computePlayerStats(
          playForInsertion.players,
          playForInsertion.resultType,
          distanceTraveled
        ),
        ...getFumbleOrPenaltyYardline(
          this.teams,
          { number: preceedingPlay.resultingYardLine, side: preceedingSide },
          playForInsertion
        ),
      };

      adjustPlayerStats(playInsert, playForInsertion);

      // for TDs/XPs/Kickoffs some starting/res down/distances will be null
      nullifyDownAndDistanceIfNeccessary(playInsert);

      if (playInsert.playDesignId === TPlayDesigns.KICKOFF && playInsert.drivePlayNumber !== 0) {
        // ensure drive play number is 0 for kickoffs
        const { nextDriveNumber } = getDriveNumber(
          playInsert,
          playInsert.driveNumber,
          playInsert.drivePlayNumber,
          true,
          this.plays,
          true
        );
        playInsert.driveNumber = nextDriveNumber;
        playInsert.drivePlayNumber = 0;
      }

      this.plays.splice(preceedingPlayNdx + 1, 0, playInsert);
      this.inserting = null;

      // if adding after the most recent play, update the current state
      if (preceedingPlayNdx === this.plays.length - 1) {
        this.revertToLastValidPlayState(preceedingPlayNdx - 1);
        this.updatePlayState(
          gameId,
          playForInsertion,
          playInsert,
          playInsert.homeTeamStartedWithPossession ? TTeam.HOME : TTeam.AWAY,
          isTurnoverOnDowns,
          playInsert.resultingDown,
          playInsert.resultingDistance,
          playInsert.resultingYardLine,
          true
        );
        this.setCurrentPlayNumbering(gameId, playInsert.driveNumber, playInsert.drivePlayNumber + 1);
      }

      this.queuePlayflowUpdate(gameId, playInsert);
    },

    async commitPlayEdit(gameId: number, editedPlay: IMachineContextCurrentPlay) {
      const originalPlayNdx = findIndex(
        this.plays,
        (p) => p.drivePlayNumber === this.editing.play && p.driveNumber === this.editing.drive
      );
      const preceedingValidPlay = findLast(
        this.plays.slice(0, originalPlayNdx),
        (play) => !play.isPlayNullified
      );
      const originalPlay = this.plays[originalPlayNdx];

      const isPriorPossessionHome =
        preceedingValidPlay?.homeTeamEndedWithPossession ?? originalPlay.homeTeamStartedWithPossession;
      const isPriorSideHome =
        preceedingValidPlay?.resultingIsHomeTeamSideOfField ?? originalPlay.startingIsHomeTeamSideOfField;
      const priorDown = preceedingValidPlay?.resultingDown ?? originalPlay.startingDown;
      const priorDistance = preceedingValidPlay?.resultingDistance ?? originalPlay.startingDistance;
      const priorYardline = preceedingValidPlay?.resultingYardLine ?? originalPlay.startingYardLine;

      const originalActiveTeam = isPriorPossessionHome ? TTeam.HOME : TTeam.AWAY;
      const driveDirection = determinePlayDriveDirection(originalPlay, this.teams);

      let originalSide = TTeamSide.LEFT;
      if (
        (isPriorSideHome && this.teams.right === TTeam.HOME) ||
        (!isPriorSideHome && this.teams.left === TTeam.HOME)
      ) {
        originalSide = TTeamSide.RIGHT;
      }

      const {
        resultingDistance,
        resultingDown,
        homeTeamEndedWithPossession,
        isTurnoverOnDowns,
        distanceTraveled,
      } = getPlayResults(
        editedPlay,
        originalActiveTeam,
        priorDown,
        priorDistance,
        driveDirection,
        priorYardline,
        originalSide
      );

      const resultingYardLine = getResultingYardline(editedPlay);
      const resultingIsHomeTeamSideOfField = getResultingSideOfField(
        this.teams,
        editedPlay.playDesignId,
        editedPlay.postPlaySide,
        editedPlay.penalty === TOffDef.DEFENSE
      );

      const playEdit: IPlayflowPlay = {
        driveNumber: this.editing.drive,
        drivePlayNumber: this.editing.play,
        startingYardLine: preceedingValidPlay?.resultingYardLine ?? originalPlay.startingYardLine,
        startingDown: preceedingValidPlay?.resultingDown ?? originalPlay.startingDown,
        startingDistance: preceedingValidPlay?.resultingDistance ?? originalPlay.resultingDistance,
        startingIsHomeTeamSideOfField:
          preceedingValidPlay?.resultingIsHomeTeamSideOfField ?? originalPlay.startingIsHomeTeamSideOfField,
        homeTeamStartedWithPossession:
          preceedingValidPlay?.homeTeamEndedWithPossession ?? originalPlay.homeTeamStartedWithPossession,
        isPlayNullified: false,
        resultingYardLine,
        resultingDown,
        resultingDistance,
        resultingIsHomeTeamSideOfField,
        homeTeamEndedWithPossession,
        playResultTypeId: getPlayResultId(editedPlay, this.currentTeamRoles.offense, isTurnoverOnDowns),
        refEventOutcomeId: editedPlay.refEventOutcomeId || originalPlay.refEventOutcomeId || null,
        playDesignId: editedPlay.playDesignId || TPlayDesigns.NO_PLAY,
        lateral: !!editedPlay.lateral,
        qb1Out:
          editedPlay.options?.isNonQb1Pass ?? this.isQb1Out[this.presentStateBasedOnCurrentMode.activeTeam],
        playerStats: computePlayerStats(editedPlay.players, editedPlay.resultType, distanceTraveled),
        ...getFumbleOrPenaltyYardline(
          this.teams,
          { number: originalPlay.startingYardLine, side: originalSide },
          editedPlay
        ),
      };

      adjustPlayerStats(playEdit, editedPlay);

      // for TDs/XPs/Kickoffs some starting/res down/distances will be null
      nullifyDownAndDistanceIfNeccessary(playEdit);

      this.plays.splice(originalPlayNdx, 1, playEdit);
      this.editing = null;

      this.pushEvent(
        {
          eventTypeId: TEventTypes.REVISION,
          value: true,
        },
        gameId
      );

      const nextPlay = this.plays[originalPlayNdx + 1];

      // if most recent play was edited, update the current state
      if (originalPlayNdx === this.plays.length - 1) {
        this.revertToLastValidPlayState(originalPlayNdx);
        this.updatePlayState(
          gameId,
          editedPlay,
          playEdit,
          playEdit.homeTeamStartedWithPossession ? TTeam.HOME : TTeam.AWAY,
          isTurnoverOnDowns,
          playEdit.resultingDown,
          playEdit.resultingDistance,
          playEdit.resultingYardLine,
          true
        );

        const { nextDriveNumber, nextDrivePlayNumber } = getDriveNumber(
          playEdit,
          playEdit.driveNumber,
          playEdit.drivePlayNumber
        );

        this.setCurrentPlayNumbering(gameId, nextDriveNumber, nextDrivePlayNumber);
      } else if (![TPlayDesigns.KICKOFF, TPlayDesigns.ONSIDE].includes(nextPlay.playDesignId)) {
        // if not most recent update the next play starting info
        const updatedNextPlay: IPlayflowPlay = {
          ...nextPlay,
          startingDistance: resultingDistance,
          startingDown: resultingDown,
          startingIsHomeTeamSideOfField: resultingIsHomeTeamSideOfField,
          startingYardLine: resultingYardLine,
          homeTeamStartedWithPossession: homeTeamEndedWithPossession,
        };
        this.plays.splice(originalPlayNdx + 1, 1, updatedNextPlay);

        this.queuePlayflowUpdate(gameId, updatedNextPlay, true);
      }

      this.queuePlayflowUpdate(gameId, playEdit, true);
    },

    startHalf2(gameId: number) {
      this.down = 1;
      this.distance = FIRST_DOWN_YARDAGE;
      this.flipField();

      if (
        // Home Team Kicked at Start of Game and Ended first half with possession
        (this.plays[0].homeTeamStartedWithPossession && this.lastValidPlay.homeTeamEndedWithPossession) ||
        // Away Team Kicked at Start of Game and Ended first half with possession
        (!this.plays[0].homeTeamStartedWithPossession && !this.lastValidPlay.homeTeamEndedWithPossession)
      ) {
        this.switchActiveTeam();
        this.setDriveDirection(this.driveDirection === TTeamSide.LEFT ? TTeamSide.RIGHT : TTeamSide.LEFT);
      }
      const side = this.teams.left === this.activeTeam ? TTeamSide.LEFT : TTeamSide.RIGHT;
      this.setYardline(KICKOFF_YARDLINE, side);
    },

    startOT1(gameId: number, oppTeamSide: TTeamSide) {
      this.hasStartedOvertime = true;
      this.setYardline(TOUCHBACK_YARDLINE, oppTeamSide);
      this.down = 1;
      this.distance = FIRST_DOWN_YARDAGE;
      this.incrementDriveNumber(gameId);

      const lastValidPlay = findLast(this.plays, (play) => !play.isPlayNullified);
      if (!lastValidPlay) {
        return;
      }

      lastValidPlay.resultingDistance = this.distance;
      lastValidPlay.resultingDown = this.down;
      lastValidPlay.resultingYardLine = this.yardLine.number;
      lastValidPlay.resultingIsHomeTeamSideOfField = this.teams[this.yardLine.side] === TTeam.HOME;

      this.queuePlayflowUpdate(gameId, lastValidPlay, true);
    },

    startOT2(gameId: number) {
      const nextTeam = this.getNextOvertimeTeam();
      const oppTeamSide = nextTeam === this.teams.left ? TTeamSide.RIGHT : TTeamSide.LEFT;
      this.setYardline(TOUCHBACK_YARDLINE, oppTeamSide);
      this.down = 1;
      this.distance = FIRST_DOWN_YARDAGE;
      this.incrementDriveNumber(gameId);
      this.driveDirection = oppTeamSide;
      this.activeTeam = nextTeam;

      const lastValidPlay = findLast(this.plays, (play) => !play.isPlayNullified);
      if (!lastValidPlay) {
        return;
      }

      lastValidPlay.resultingDistance = this.distance;
      lastValidPlay.resultingDown = this.down;
      lastValidPlay.resultingYardLine = this.yardLine.number;
      lastValidPlay.resultingIsHomeTeamSideOfField = this.teams[this.yardLine.side] === TTeam.HOME;
      lastValidPlay.homeTeamEndedWithPossession = nextTeam === TTeam.HOME;

      this.queuePlayflowUpdate(gameId, lastValidPlay, true);
    },

    startOT3(gameId: number) {
      const nextTeam = this.getNextOvertimeTeam();
      const oppTeamSide = nextTeam === this.teams.left ? TTeamSide.RIGHT : TTeamSide.LEFT;
      this.setYardline(PAT_YARDLINE, oppTeamSide);
      this.driveDirection = oppTeamSide;
      this.activeTeam = nextTeam;
      this.down = null;
      this.distance = null;

      const lastValidPlay = findLast(this.plays, (play) => !play.isPlayNullified);
      if (!lastValidPlay) {
        return;
      }

      lastValidPlay.resultingDistance = null;
      lastValidPlay.resultingDown = null;
      lastValidPlay.resultingYardLine = this.yardLine.number;
      lastValidPlay.resultingIsHomeTeamSideOfField = this.teams[this.yardLine.side] === TTeam.HOME;
      lastValidPlay.homeTeamEndedWithPossession = nextTeam === TTeam.HOME;

      this.queuePlayflowUpdate(gameId, lastValidPlay, true);
    },
  },

  getters: {
    currentPlayInfo(state) {
      return {
        driveNumber: state.driveNumber,
        drivePlayNumber: state.drivePlayNumber,
        startingYardLine: state.yardLine.number,
        startingDown: state.down,
        startingDistance: state.distance,
        startingIsHomeTeamSideOfField: state.teams[state.yardLine.side] === TTeam.HOME,
        homeTeamStartedWithPossession: state.activeTeam === TTeam.HOME,
        isPlayNullified: false,
      };
    },

    lastPlay(state): IPlayflowPlay {
      return last(state.plays);
    },

    lastValidPlay(state): IPlayflowPlay {
      return findLast(state.plays, (play) => !play.isPlayNullified);
    },

    lastValidRealPlay(state): IPlayflowPlay {
      return findLast(
        state.plays,
        (play) => !play.isPlayNullified && play.playDesignId !== TPlayDesigns.NO_PLAY
      );
    },

    isEditingMostRecentPlay(state): boolean {
      if (!state.editing) {
        return false;
      }
      const lastPlay = findLast(state.plays, (play) => !play.isPlayNullified);
      return state.editing.drive === lastPlay.driveNumber && state.editing.play === lastPlay.drivePlayNumber;
    },

    currentTeamRoles(state): { offense: TTeam; defense: TTeam } {
      if (state.inserting) {
        const insertingAfterPlay = find(
          state.plays,
          (p) =>
            p.drivePlayNumber === state.inserting.beforePlay && p.driveNumber === state.inserting.beforeDrive
        );

        return {
          offense: insertingAfterPlay.homeTeamEndedWithPossession ? TTeam.HOME : TTeam.AWAY,
          defense: insertingAfterPlay.homeTeamEndedWithPossession ? TTeam.AWAY : TTeam.HOME,
        };
      }

      if (state.editing) {
        const editingPlayNdx = findIndex(
          state.plays,
          (p) => p.drivePlayNumber === state.editing.play && p.driveNumber === state.editing.drive
        );
        const editingPlay = state.plays[editingPlayNdx];

        let activeTeam = editingPlay.homeTeamStartedWithPossession ? TTeam.HOME : TTeam.AWAY;

        if (editingPlayNdx > 0) {
          const preceedingValidPlay = findLast(
            state.plays.slice(0, editingPlayNdx),
            (play) => !play.isPlayNullified
          );

          activeTeam = preceedingValidPlay.homeTeamEndedWithPossession ? TTeam.HOME : TTeam.AWAY;
        }

        return {
          offense: activeTeam === TTeam.HOME ? TTeam.HOME : TTeam.AWAY,
          defense: activeTeam === TTeam.HOME ? TTeam.AWAY : TTeam.HOME,
        };
      }

      return {
        offense: state.activeTeam === TTeam.HOME ? TTeam.HOME : TTeam.AWAY,
        defense: state.activeTeam === TTeam.HOME ? TTeam.AWAY : TTeam.HOME,
      };
    },

    presentStateBasedOnCurrentMode(
      state
    ): Pick<IPlayFlow, 'activeTeam' | 'down' | 'distance' | 'yardLine' | 'driveDirection' | 'isQb1Out'> {
      if (state.inserting) {
        const insertingAfterPlayNdx = findIndex(
          state.plays,
          (p) =>
            p.drivePlayNumber === state.inserting.beforePlay && p.driveNumber === state.inserting.beforeDrive
        );
        const insertingAfterPlay = this.plays[insertingAfterPlayNdx];

        let side = TTeamSide.RIGHT;
        if (
          (insertingAfterPlay.resultingIsHomeTeamSideOfField && state.teams.left === TTeam.HOME) ||
          (!insertingAfterPlay.resultingIsHomeTeamSideOfField && state.teams.right === TTeam.HOME)
        ) {
          side = TTeamSide.LEFT;
        }

        const isQb1Out = {
          home:
            findLast(
              this.plays.slice(0, insertingAfterPlayNdx + 1),
              (play) => play.homeTeamStartedWithPossession
            )?.qb1Out ?? false,
          away:
            findLast(
              this.plays.slice(0, insertingAfterPlayNdx + 1),
              (play) => !play.homeTeamStartedWithPossession
            )?.qb1Out ?? false,
        };

        return {
          activeTeam: insertingAfterPlay.homeTeamEndedWithPossession ? TTeam.HOME : TTeam.AWAY,
          down: insertingAfterPlay.resultingDown,
          distance: insertingAfterPlay.resultingDistance,
          yardLine: {
            number: insertingAfterPlay.resultingYardLine,
            side,
          },
          driveDirection: determinePlayDriveDirection(insertingAfterPlay, state.teams, true),
          isQb1Out,
        };
      }

      if (state.editing) {
        const editingPlayNdx = findIndex(
          state.plays,
          (p) => p.drivePlayNumber === state.editing.play && p.driveNumber === state.editing.drive
        );
        const editingPlay = state.plays[editingPlayNdx];

        let activeTeam = editingPlay.homeTeamStartedWithPossession ? TTeam.HOME : TTeam.AWAY;
        let down = editingPlay.startingDown;
        let distance = editingPlay.startingDistance;
        let side = TTeamSide.RIGHT;
        let number = editingPlay.startingYardLine;
        let isQb1Out = {
          home:
            findLast(this.plays.slice(0, editingPlayNdx + 1), (play) => play.homeTeamStartedWithPossession)
              ?.qb1Out ?? false,
          away:
            findLast(this.plays.slice(0, editingPlayNdx + 1), (play) => !play.homeTeamStartedWithPossession)
              ?.qb1Out ?? false,
        };

        if (
          (editingPlay.startingIsHomeTeamSideOfField && state.teams.left === TTeam.HOME) ||
          (!editingPlay.startingIsHomeTeamSideOfField && state.teams.right === TTeam.HOME)
        ) {
          side = TTeamSide.LEFT;
        }

        if (editingPlayNdx > 0) {
          const preceedingValidPlayNdx = findLastIndex(
            state.plays.slice(0, editingPlayNdx),
            (play) => !play.isPlayNullified
          );
          const preceedingValidPlay = this.plays[preceedingValidPlayNdx];

          activeTeam = preceedingValidPlay.homeTeamEndedWithPossession ? TTeam.HOME : TTeam.AWAY;
          down = preceedingValidPlay.resultingDown;
          distance = preceedingValidPlay.resultingDistance;
          number = preceedingValidPlay.resultingYardLine;
          side = TTeamSide.RIGHT;
          isQb1Out = {
            home:
              findLast(this.plays.slice(0, editingPlayNdx + 1), (play) => play.homeTeamStartedWithPossession)
                ?.qb1Out ?? false,
            away:
              findLast(this.plays.slice(0, editingPlayNdx + 1), (play) => !play.homeTeamStartedWithPossession)
                ?.qb1Out ?? false,
          };

          if (
            (preceedingValidPlay.resultingIsHomeTeamSideOfField && state.teams.left === TTeam.HOME) ||
            (!preceedingValidPlay.resultingIsHomeTeamSideOfField && state.teams.right === TTeam.HOME)
          ) {
            side = TTeamSide.LEFT;
          }
        }

        return {
          activeTeam,
          down,
          distance,
          yardLine: {
            number,
            side,
          },
          driveDirection: determinePlayDriveDirection(editingPlay, state.teams),
          isQb1Out,
        };
      }

      return {
        activeTeam: state.activeTeam,
        down: state.down,
        distance: state.distance,
        yardLine: state.yardLine,
        driveDirection: state.driveDirection,
        isQb1Out: state.isQb1Out,
      };
    },
  },
});
